import { type ReactNode, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ShoppingCart,
  ClipboardCheck,
  Truck,
  TrendingUp,
  Boxes,
  Settings,
} from 'lucide-react';
import { useWebSocket } from '../hooks/useWebSocket';
import { ConnectionStatus } from './ConnectionStatus';

interface NavItem {
  label: string;
  href: string;
  icon: typeof ShoppingCart;
}

const navItems: NavItem[] = [
  { label: 'To Order', href: '/', icon: ShoppingCart },
  { label: 'Confirmation', href: '/confirmation', icon: ClipboardCheck },
  { label: 'Shipment', href: '/shipment', icon: Truck },
  { label: 'Forecast', href: '/forecast', icon: TrendingUp },
  { label: 'Inventory', href: '/inventory', icon: Boxes },
];

function NavLink({ item }: { item: NavItem }) {
  const location = useLocation();
  const isActive = location.pathname === item.href;
  const Icon = item.icon;

  return (
    <Link
      to={item.href}
      className={`
        flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors
        ${
          isActive
            ? 'bg-primary text-primary-foreground'
            : 'text-foreground-muted hover:bg-background-subtle hover:text-foreground'
        }
      `}
    >
      <Icon className="h-4 w-4" />
      {item.label}
    </Link>
  );
}

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  // Subscribe to schedule channel for real-time updates
  const channels = useMemo(() => ['schedule'], []);
  const { connectionState, lastConnectedAt, reconnect } = useWebSocket(channels);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="h-14 border-b border-border px-4 flex items-center justify-between bg-background sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <span className="text-sm font-bold text-primary-foreground">S</span>
            </div>
            <span className="text-lg font-semibold text-foreground">
              Scheduler
            </span>
          </Link>
          <span className="text-xs font-medium text-foreground-muted bg-background-subtle px-2 py-1 rounded">
            Procurement
          </span>
        </div>

        <div className="flex items-center gap-4">
          <ConnectionStatus
            connectionState={connectionState}
            lastConnectedAt={lastConnectedAt}
            onReconnect={reconnect}
          />
          <button
            className="p-2 rounded-md hover:bg-background-subtle transition-colors"
            aria-label="Settings"
          >
            <Settings className="h-5 w-5 text-foreground-muted" />
          </button>
          <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
            <span className="text-sm font-medium text-primary-foreground">P</span>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-64 border-r border-border p-4 hidden lg:block bg-background">
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main id="main-content" className="flex-1 p-6 bg-background-subtle">
          {children}
        </main>
      </div>
    </div>
  );
}
